(() => {
  if (window.__postorCartUpsellInitializedV1) return;
  window.__postorCartUpsellInitializedV1 = true;

  const cache = new Map();
  const impressed = new Set();
  let observedDrawer = null;
  let observer = null;
  let refreshTimer = null;
  let requestVersion = 0;

  const shopRoot = () => {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof root === 'string' && root ? root : '/';
  };

  function emit(eventName, payload) {
    const data = Object.assign({ source: 'cart_drawer', path: window.location.pathname || '/' }, payload || {});

    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, data);
      }
    } catch (error) {}

    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: eventName }, data));
      }
    } catch (error) {}

    try {
      window.dispatchEvent(new CustomEvent(eventName, { detail: data }));
    } catch (error) {}
  }

  function formatMoney(cents, currency) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';

    try {
      return new Intl.NumberFormat('en-PK', {
        style: 'currency',
        currency: currency || 'PKR',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 0
      }).format(amount / 100);
    } catch (error) {
      return `Rs.${Math.round(amount / 100).toLocaleString('en-PK')}`;
    }
  }

  function imageUrl(product) {
    const featured = product && product.featured_image;
    if (typeof featured === 'string') return featured;
    if (featured && typeof featured === 'object') return featured.src || featured.url || '';

    const first = product && Array.isArray(product.images) ? product.images[0] : null;
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object') return first.src || first.url || '';
    return '';
  }

  async function fetchJson(url, options) {
    const response = await fetch(url, Object.assign({ credentials: 'same-origin' }, options || {}));
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = data && (data.description || data.message || data.status);
      throw new Error(message || 'Request failed');
    }
    return data;
  }

  async function getCart() {
    return fetchJson(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } });
  }

  async function getRecommendations(productId, intent) {
    const url = `${shopRoot()}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=6&intent=${encodeURIComponent(intent)}`;
    const data = await fetchJson(url, { headers: { Accept: 'application/json' } });
    return data && Array.isArray(data.products) ? data.products : [];
  }

  async function buildRecommendationSet(cart) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    const anchor = items.find((item) => item && item.product_id);
    if (!anchor) return { signature: '', items: [] };

    const cartIds = new Set(items.map((item) => String(item.product_id)));
    const signature = Array.from(cartIds).sort().join('|');
    const cacheKey = `${anchor.product_id}:${signature}`;
    if (cache.has(cacheKey)) return Object.assign({ signature }, cache.get(cacheKey));

    const selected = [];
    const used = new Set();

    const addCandidates = (products, intent) => {
      products.forEach((product) => {
        if (selected.length >= 2 || !product || product.id == null) return;
        const id = String(product.id);
        if (cartIds.has(id) || used.has(id)) return;
        used.add(id);
        selected.push({ product, intent });
      });
    };

    try {
      addCandidates(await getRecommendations(anchor.product_id, 'complementary'), 'complementary');
    } catch (error) {}

    if (selected.length < 2) {
      try {
        addCandidates(await getRecommendations(anchor.product_id, 'related'), 'related');
      } catch (error) {}
    }

    const result = { items: selected.slice(0, 2) };
    cache.set(cacheKey, result);
    return Object.assign({ signature }, result);
  }

  function productLink(product) {
    return product && typeof product.url === 'string' && product.url ? product.url : '/collections/all';
  }

  function createTextLink(product, className, text) {
    const link = document.createElement('a');
    link.className = className;
    link.href = productLink(product);
    link.textContent = text;
    return link;
  }

  function trackProductClick(product, intent, variantId) {
    emit('postor_cart_upsell_click', {
      product_id: product && product.id != null ? String(product.id) : '',
      variant_id: variantId != null ? String(variantId) : '',
      recommendation_intent: intent
    });
  }

  async function addVariant(button, product, variant, intent, status) {
    if (!button || button.disabled || !variant || variant.id == null) return;

    trackProductClick(product, intent, variant.id);
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding…';
    if (status) status.textContent = '';

    try {
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map((section) => section.id)
        : [];

      const payload = {
        items: [{ id: Number(variant.id), quantity: 1 }],
        sections,
        sections_url: window.location.pathname || '/'
      };

      const data = await fetchJson(`${shopRoot()}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      emit('postor_cart_upsell_add', {
        product_id: String(product.id),
        variant_id: String(variant.id),
        recommendation_intent: intent
      });

      cache.clear();

      if (drawer && typeof drawer.renderContents === 'function' && data && data.sections) {
        drawer.renderContents(data);
      } else {
        window.location.reload();
      }
    } catch (error) {
      button.disabled = false;
      button.textContent = originalText;
      if (status) status.textContent = 'Could not add this item. Please try again.';
    }
  }

  function buildCard(entry, currency) {
    const product = entry.product;
    const intent = entry.intent;
    const card = document.createElement('article');
    card.className = 'postor-cart-upsell__card';
    card.dataset.productId = String(product.id);
    card.dataset.intent = intent;

    const media = document.createElement('a');
    media.className = 'postor-cart-upsell__media';
    media.href = productLink(product);
    media.setAttribute('aria-label', product.title || 'View product');
    media.addEventListener('click', () => trackProductClick(product, intent, ''));

    const src = imageUrl(product);
    if (src) {
      const image = document.createElement('img');
      image.className = 'postor-cart-upsell__image';
      image.src = src;
      image.alt = product.title || '';
      image.loading = 'lazy';
      media.appendChild(image);
    }

    const content = document.createElement('div');
    content.className = 'postor-cart-upsell__content';

    const title = createTextLink(product, 'postor-cart-upsell__title', product.title || 'View product');
    title.addEventListener('click', () => trackProductClick(product, intent, ''));
    content.appendChild(title);

    const price = document.createElement('p');
    price.className = 'postor-cart-upsell__price';
    price.textContent = formatMoney(product.price, currency);
    content.appendChild(price);

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const singleVariant = variants.length === 1 ? variants[0] : null;
    const canQuickAdd = singleVariant && singleVariant.available !== false;
    let action;

    const status = document.createElement('p');
    status.className = 'postor-cart-upsell__status';
    status.setAttribute('aria-live', 'polite');

    if (canQuickAdd) {
      action = document.createElement('button');
      action.type = 'button';
      action.className = 'postor-cart-upsell__action';
      action.textContent = 'Add';
      action.addEventListener('click', () => addVariant(action, product, singleVariant, intent, status));
    } else {
      action = createTextLink(product, 'postor-cart-upsell__action', variants.length > 1 ? 'Choose options' : 'View product');
      action.addEventListener('click', () => trackProductClick(product, intent, singleVariant && singleVariant.id));
    }

    card.append(media, content, action);
    if (canQuickAdd) content.appendChild(status);
    return card;
  }

  function render(drawer, recommendationSet, currency) {
    const existing = drawer.querySelector('.postor-cart-upsell');
    if (existing) existing.remove();

    if (!recommendationSet.items.length) return;
    const wrapper = drawer.querySelector('.drawer__cart-items-wrapper');
    if (!wrapper) return;

    const block = document.createElement('section');
    block.className = 'postor-cart-upsell';
    block.dataset.signature = recommendationSet.signature;
    block.setAttribute('aria-label', 'Recommended wall art');

    const hasComplementary = recommendationSet.items.some((entry) => entry.intent === 'complementary');
    const heading = document.createElement('h3');
    heading.className = 'postor-cart-upsell__heading';
    heading.textContent = hasComplementary ? 'Complete your wall' : 'More wall art you may like';

    const list = document.createElement('div');
    list.className = 'postor-cart-upsell__list';
    recommendationSet.items.forEach((entry) => list.appendChild(buildCard(entry, currency)));

    block.append(heading, list);
    wrapper.insertAdjacentElement('afterend', block);

    const impressionKey = `${recommendationSet.signature}:${recommendationSet.items.map((entry) => entry.product.id).join(',')}`;
    if (!impressed.has(impressionKey)) {
      impressed.add(impressionKey);
      recommendationSet.items.forEach((entry) => {
        emit('postor_cart_upsell_impression', {
          product_id: String(entry.product.id),
          variant_id: '',
          recommendation_intent: entry.intent
        });
      });
    }
  }

  async function refresh() {
    const version = ++requestVersion;
    const drawer = document.querySelector('#CartDrawer.cart-drawer');
    if (!drawer) return;

    try {
      const cart = await getCart();
      if (version !== requestVersion) return;

      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
        const existing = drawer.querySelector('.postor-cart-upsell');
        if (existing) existing.remove();
        return;
      }

      const signature = Array.from(new Set(cart.items.map((item) => String(item.product_id)))).sort().join('|');
      const existing = drawer.querySelector('.postor-cart-upsell');
      if (existing && existing.dataset.signature === signature) return;

      const recommendationSet = await buildRecommendationSet(cart);
      if (version !== requestVersion) return;
      render(drawer, recommendationSet, cart.currency || 'PKR');
    } catch (error) {}
  }

  function scheduleRefresh(delay) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, typeof delay === 'number' ? delay : 140);
  }

  function attachObserver() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer === observedDrawer) return;

    if (observer) observer.disconnect();
    observedDrawer = drawer;
    observer = new MutationObserver(() => scheduleRefresh(160));
    observer.observe(drawer, { childList: true, subtree: true });
  }

  function init() {
    attachObserver();
    scheduleRefresh(0);

    const bodyObserver = new MutationObserver(() => {
      attachObserver();
      scheduleRefresh(160);
    });
    bodyObserver.observe(document.body, { childList: true, subtree: false });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
